import './dashboard.css';
import { useUser } from '../../UserContext';
import { useEffect, useMemo, useState } from 'react';
import { get, getPhotosBaseUrl } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import { useNavigate } from 'react-router-dom';
import courseCompletedImage from '../../imgs/course-completed.png';
import startCourseImage from '../../imgs/start-course.jpg';

function Dashboard() {
    const navigate = useNavigate();
    const { user } = useUser();
    const { setLoading, setMessage } = useUI();
    const [pageLoaded, setPageLoaded] = useState(false);
    const [courseProgress, setCourseProgress] = useState(null);
    const [currentClass, setCurrentClass] = useState(null);
    const [news, setNews] = useState([]);
    const currentHour = new Date().getHours();
    const userName = user?.name?.trim();

    const greeting = currentHour >= 5 && currentHour < 12
        ? 'Bom dia'
        : currentHour >= 12 && currentHour < 18
            ? 'Boa tarde'
            : 'Boa noite';

    const greetingWithName = userName
        ? `${greeting}, ${userName}`
        : greeting;

    useEffect(() => {
        let active = true;

        async function getDashboardData() {
            setLoading(true);
            setPageLoaded(false);

            let loadedCourseProgress = null;
            let loadedCurrentClass = null;
            let loadedNews = [];

            await get('/video-progress/course', true, setMessage,
                (response) => {
                    loadedCourseProgress = response.data || null;
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar o progresso do curso: ${err}`);
                },
                () => {},
                'logout',
                'none'
            );

            const videoId = loadedCourseProgress?.videoProgress?.videoId;

            if (videoId) {
                await get(`/module/class/video/${videoId}`, true, setMessage,
                    (response) => {
                        loadedCurrentClass = response.data || null;
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível carregar os detalhes da aula pelo vídeo atual: ${err}`);
                    },
                    () => {},
                    'logout',
                    'none'
                );
            }

            await get('/news/search?page=1&pageSize=3', true, setMessage,
                (response) => {
                    const loadedNewsResponse = response.data;
                    loadedNews = Array.isArray(loadedNewsResponse)
                        ? loadedNewsResponse
                        : loadedNewsResponse?.items || [];
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar as últimas notícias: ${err}`);
                },
                () => {},
                'logout',
                'none'
            );

            if (!active)
                return;

            setCourseProgress(loadedCourseProgress);
            setCurrentClass(loadedCurrentClass);
            setNews(loadedNews.slice(0, 3));
            setPageLoaded(true);
            setLoading(false);
        }

        getDashboardData();

        return () => {
            active = false;
            setLoading(false);
        };
    }, [setLoading, setMessage]);

    const videoProgress = courseProgress?.videoProgress || null;
    const courseCompleted = !!courseProgress?.completed;
    const isStartingJourney = !courseCompleted && !videoProgress;

    const progressTitle = useMemo(() => {
        if (courseCompleted)
            return 'Curso concluído';

        if (!videoProgress)
            return 'Comece a sua jornada';

        return 'Continue de onde parou';
    }, [courseCompleted, videoProgress]);

    const progressPercent = useMemo(() => {
        if (!videoProgress)
            return 0;

        const duration = Number(videoProgress.duration);
        const lastSecond = Number(videoProgress.lastSecond);

        if (!duration || duration <= 0)
            return 0;

        return Math.min(100, Math.max(0, Math.round((lastSecond / duration) * 100)));
    }, [videoProgress]);

    const className = useMemo(() => {
        if (!currentClass)
            return undefined;

        if (typeof currentClass.name === 'string')
            return currentClass.name;

        if (typeof currentClass.className === 'string')
            return currentClass.className;

        if (typeof currentClass.title === 'string')
            return currentClass.title;

        if (typeof currentClass.class?.name === 'string')
            return currentClass.class.name;

        return undefined;
    }, [currentClass]);

    const moduleName = useMemo(() => {
        if (!currentClass)
            return undefined;

        if (typeof currentClass.module?.name === 'string')
            return currentClass.module.name;

        if (typeof currentClass.moduleName === 'string')
            return currentClass.moduleName;

        if (typeof currentClass.class?.module?.name === 'string')
            return currentClass.class.module.name;

        return undefined;
    }, [currentClass]);

    const classDisplayName = useMemo(() => {
        if (moduleName && className)
            return `${moduleName}, ${className}`;

        return className || moduleName || undefined;
    }, [moduleName, className]);

    function getModuleImageName(module) {
        const imagePath = module?.imagePath;

        if (!imagePath)
            return undefined;

        const imagePathSplitted = imagePath.split('/');
        return imagePathSplitted[imagePathSplitted.length - 1];
    }

    function getCurrentModuleImage() {
        const moduleData = currentClass?.module || currentClass?.class?.module;
        const imageName = getModuleImageName(moduleData);

        if (!imageName)
            return undefined;

        return `${getPhotosBaseUrl()}/modules/${imageName}`;
    }

    function getCurrentClassModuleId() {
        if (!currentClass)
            return undefined;

        return currentClass.moduleId
            || currentClass.module?.id
            || currentClass.module?.moduleId
            || currentClass.class?.moduleId
            || currentClass.class?.module?.id;
    }

    function onClickWatchClass() {
        const moduleId = getCurrentClassModuleId();

        if (moduleId) {
            navigate(`/module/${moduleId}`);
            return;
        }

        navigate('/modules');
    }

    function getNewsTitle(newsItem) {
        return newsItem?.title || newsItem?.name || 'Sem título';
    }

    function getNewsUpdatedAt(newsItem) {
        if (!newsItem?.updatedAt)
            return undefined;

        const date = new Date(newsItem.updatedAt);

        if (Number.isNaN(date.getTime()))
            return undefined;

        return date.toLocaleDateString('pt-BR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    }

    function onClickNews(newsItem) {
        navigate(`/news/${newsItem.id}`);
    }

    function renderNewsCard(newsItem, variant = 'default') {
        const updatedAt = getNewsUpdatedAt(newsItem);
        const cardClassName =
            variant === 'featured'
                ? 'dashboard-news-card dashboard-news-card-featured container-white zoom-effect'
                : 'dashboard-news-card dashboard-news-card-small container-white zoom-effect';

        return (
            <button
                type='button'
                key={newsItem.id || getNewsTitle(newsItem)}
                className={cardClassName}
                onClick={() => onClickNews(newsItem)}>
                {variant === 'featured' && (
                    <p className='dashboard-progress-status'>Últimas notícias e artigos</p>
                )}

                <h2>{getNewsTitle(newsItem)}</h2>

                {updatedAt && (
                    <p className='dashboard-news-date'>{updatedAt}</p>
                )}
            </button>
        );
    }

    if (!pageLoaded)
        return null;

    const firstNews = news[0];
    const secondaryNews = news.slice(1, 3);
    const moduleImage = getCurrentModuleImage();
    const watchClassButtonLabel = (courseCompleted || isStartingJourney) ? 'Aulas' : 'Assistir agora';

    function renderProgressActions() {
        return (
            <div className='dashboard-progress-module-actions'>
                {videoProgress && (
                    <div className='dashboard-progress-bar-shell'>
                        <div className='dashboard-progress-bar-track'>
                            <div
                                className='dashboard-progress-bar-fill'
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>

                        <p className='dashboard-progress-percent'>{progressPercent}%</p>
                    </div>
                )}

                <button
                    type='button'
                    className='blue-button zoom-effect dashboard-watch-class-button'
                    onClick={onClickWatchClass}>
                    {watchClassButtonLabel}
                </button>
            </div>
        );
    }

    return (
        <main className="dashboard-content">

            <h1 className="dashboard-greeting-title">{greetingWithName}.</h1>
            <p className="dashboard-greeting-title">Pronto para continuar a sua jornada?</p>

            <section className='dashboard-cards-layout'>
                <article className='dashboard-progress-card container-white'>
                    <header>
                        <p className='dashboard-progress-status'>{progressTitle}</p>
                    </header>

                    {courseCompleted ? (
                        <div className='dashboard-course-completed-image-wrapper'>
                            <img
                                src={courseCompletedImage}
                                alt='Curso concluído'
                                className='dashboard-course-completed-image'
                            />

                            <div className='dashboard-progress-module-overlay'>
                                {renderProgressActions()}
                            </div>
                        </div>
                    ) : isStartingJourney ? (
                        <div className='dashboard-course-completed-image-wrapper'>
                            <img
                                src={startCourseImage}
                                alt='Comece a sua jornada'
                                className='dashboard-course-completed-image'
                            />

                            <div className='dashboard-progress-module-overlay'>
                                {renderProgressActions()}
                            </div>
                        </div>
                    ) : videoProgress && (
                        <div className='dashboard-progress-class'>
                            <div
                                className='dashboard-progress-module-image'
                                style={{ backgroundImage: moduleImage ? `url(${moduleImage})` : 'none' }}>
                                <div className='dashboard-progress-module-overlay'>
                                    <h2>{classDisplayName || 'Aula em andamento'}</h2>

                                    {renderProgressActions()}
                                </div>
                            </div>
                        </div>
                    )}
                </article>

                <section className='dashboard-news-column'>
                    {firstNews
                        ? renderNewsCard(firstNews, 'featured')
                        : <article className='dashboard-news-empty container-white'>Nenhuma notícia disponível.</article>}

                    <div className='dashboard-news-bottom-grid'>
                        {secondaryNews.map((newsItem) => renderNewsCard(newsItem, 'small'))}
                    </div>
                </section>
            </section>
        </main>
    )
}

export default Dashboard;